// ============================================================================
// roleOrientationAutoOpen.js — pure Role-Orientation auto-open lifecycle (Option A)
//
// Role Orientation auto-opens ONCE on the first login that has no durable
// auto-open marker, and only for users who have not already acknowledged it.
// Pure + unit-testable. The durable markers live on users/{uid}/learning/state:
//   - `roleIntroCompleted === true`        → user clicked "I understand" (primary)
//   - `roleOrientationAutoOpenedAt` present → orientation already auto-opened
// EITHER marker permanently blocks auto-open. Manual opening from Settings is
// independent of this rule and neither requires nor writes any marker.
//
// This module owns three pure pieces of the lifecycle:
//   1. shouldAutoOpenRoleOrientation() — the auto-open DECISION.
//   2. buildRoleIntroCompletionUpdate() — the durable "I understand" WRITE payload.
//   3. isLearningStateResolved() — the login read-race GUARD (decision must wait
//      for the current uid's read before it can fire).
// ============================================================================

/**
 * Decide whether Role Orientation should AUTO-open (Option A — legacy-safe).
 *
 * Auto-open iff: not loading, no read error, not dismissed this session, the
 * durable `roleOrientationAutoOpenedAt` marker is ABSENT, and the user has not
 * already completed/acknowledged the intro (`roleIntroCompleted !== true`).
 * The marker check caps auto-open to once; the roleIntroCompleted check protects
 * existing users who already acknowledged the orientation from re-opening.
 *
 * @param {{ loading?: boolean, error?: unknown, sessionDismissed?: boolean,
 *           roleOrientationAutoOpenedAt?: unknown, roleIntroCompleted?: unknown }} [state]
 * @returns {boolean}
 */
export function shouldAutoOpenRoleOrientation(state = {}) {
  const {
    loading,
    error,
    sessionDismissed,
    roleOrientationAutoOpenedAt,
    roleIntroCompleted,
  } = state || {};

  if (loading) return false;
  if (error) return false;
  if (sessionDismissed) return false;
  if (roleOrientationAutoOpenedAt != null) return false; // already auto-opened (durable marker present)
  if (roleIntroCompleted === true) return false;          // legacy guard: already acknowledged
  return true;
}

/**
 * Role-content safety: Role Orientation can only open (auto OR manual) when the
 * role actually has orientation content. Used to disable the Settings entry and
 * to gate auto-open enablement. Depends ONLY on content — never on the marker —
 * so a user who already auto-opened can still open it manually.
 *
 * @param {unknown} orientationCard the result of getOrientationCard(role)
 * @returns {boolean}
 */
export function canOpenRoleOrientation(orientationCard) {
  return !!orientationCard && typeof orientationCard === 'object';
}

/**
 * Build the durable Role-Orientation completion write for
 * users/{uid}/learning/state, used when the user clicks "I understand" in the
 * AUTO-OPEN card. This is the single durable completion writer (no duplicate
 * logic): it records acknowledgement (`roleIntroCompleted` +
 * `completedModules.role_orientation`) AND stamps the set-once auto-open marker
 * (`roleOrientationAutoOpenedAt`) when it is still absent — so EITHER source of
 * truth blocks every future auto-open (see {@link shouldAutoOpenRoleOrientation}).
 *
 * Set-once: when the marker already exists, it is OMITTED from the payload so the
 * original first-open time is preserved through the merge (we never overwrite it).
 * The payload only ever contains learning-progress fields — never PHI, workflow,
 * identity (uid/tenantId/role), preferences, or createdAt keys — so it is safe to
 * merge into the never-PHI learning doc without clobbering existing form.
 *
 * Pure: the caller injects the server-time sentinel via `now`
 * (e.g. firestore serverTimestamp()) so this stays unit-testable.
 *
 * @param {{ existing?: object|null, currentRoute?: string|null, now?: unknown }} [args]
 * @returns {Record<string, unknown>} a Firestore `{ merge: true }` payload
 */
export function buildRoleIntroCompletionUpdate({ existing = null, currentRoute = null, now = null } = {}) {
  const update = {
    roleIntroCompleted: true,
    'completedModules.role_orientation': true,
    lastSeenRoute: currentRoute || null,
    lastUpdatedAt: now,
  };
  // Stamp the durable auto-open marker only when it is absent (set-once).
  if (!existing || existing.roleOrientationAutoOpenedAt == null) {
    update.roleOrientationAutoOpenedAt = now;
  }
  return update;
}

/**
 * True only once the learning-state read for the CURRENT uid has resolved.
 *
 * The read is asynchronous: on login `uid` becomes available one render BEFORE
 * its Firestore read resolves. During that window the hook's `loading` flag can
 * be a stale `false` (left over from the logged-out branch) while the snapshot is
 * still empty — so the auto-open rule would fire against an EMPTY state and
 * re-open orientation on EVERY login, regardless of the persisted markers. Gate
 * the decision on this: feed `!isLearningStateResolved(uid, loadedUid)` into the
 * `loading` input of {@link shouldAutoOpenRoleOrientation} so it stays closed
 * until THIS uid's read has landed.
 *
 * @param {string|null|undefined} uid       current user id (null when logged out)
 * @param {string|null|undefined} loadedUid uid whose learning-state read resolved
 * @returns {boolean}
 */
export function isLearningStateResolved(uid, loadedUid) {
  if (!uid) return true;     // logged out → nothing to load
  return loadedUid === uid;  // resolved only when the loaded uid matches the current one
}
